import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

import { Loading, PageHeader, Pagination } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import useComponentState from './../../utils/useComponentState';
import { getAllUserDetails, filterUser } from './../../api/user';
import Filter from './../../components/filter/filter.component';

import styles from './userDetails.module.scss';

const UserDetails = (props) => {
  const [state, setState] = useComponentState({
    tableHeading: {
      column: ['First Name', 'Last Name', 'Email', 'Mobile Number'],
    },
    userData: [],
    userLoadingError: '',
    loading: false,
    filterLoading: false,
    currentPage: 0,
  });
  const {
    tableHeading,
    userData,
    userLoadingError,
    loading,
    filterLoading,
    currentPage,
  } = state;

  // Tracks the most recently issued request so a slower, stale response
  // (e.g. the initial full user list) can't overwrite a newer filtered result.
  const latestRequestId = useRef(0);

  const PAGE_SIZE = 10;
  const pageCount = Math.ceil(userData.length / PAGE_SIZE) || 1;
  const paginatedUserData = userData.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE
  );

  const handlePageChange = (page) => {
    setState({ currentPage: page });
  };

  useEffect(() => {
    getUserDetails();
    //eslint-disable-next-line
  }, []);

  const getUserDetails = async () => {
    const requestId = ++latestRequestId.current;
    setState({ loading: true, userData: [], userLoadingError: '' });
    try {
      const response = await getAllUserDetails(props.auth.auth.token);
      if (requestId === latestRequestId.current) {
        setState({ userData: response.users, currentPage: 0 });
      }
    } catch (error) {
      if (requestId === latestRequestId.current) {
        setState({
          userLoadingError: 'Oops, something went wrong. Please try again',
        });
      }
    }
    if (requestId === latestRequestId.current) {
      setState({ loading: false });
    }
  };

  const handleDownload = async () => {
    handleExcelSheetDownload(userData);
  };

  const handleUserDetailsFilter = async (data) => {
    const requestId = ++latestRequestId.current;
    try {
      setState({ filterLoading: true });
      const result = await filterUser(props.auth.auth.token, data);
      // The backend's email filter matches loosely (partial/substring), so an
      // exact email search still comes back with unrelated users. Narrow the
      // response down to an exact (case-insensitive) match ourselves.
      const users = data.email
        ? (result.users || []).filter(
            (user) =>
              user.email &&
              user.email.trim().toLowerCase() === data.email.trim().toLowerCase()
          )
        : result.users;
      if (requestId === latestRequestId.current) {
        setState({ userData: users, currentPage: 0 });
      }
    } catch (e) {}
    setState({ filterLoading: false });
  };

  const handleResetFilter = async () => {
    const requestId = ++latestRequestId.current;
    try {
      setState({ filterLoading: true });
      const response = await getAllUserDetails(props.auth.auth.token);
      if (requestId === latestRequestId.current) {
        setState({ userData: response.users, currentPage: 0 });
      }
    } catch (error) {
      if (requestId === latestRequestId.current) {
        setState({
          userLoadingError: 'Oops, something went wrong. Please try again',
        });
      }
    }
    if (requestId === latestRequestId.current) {
      setState({ filterLoading: false });
    }
  };

  const handleExportDate = (data) => {
    handleExcelSheetDownload(data);
  };

  const handleExcelSheetDownload = async (users) => {
    const excelDate = users.map((user) => {
      const userDetails = {
        'First Name': user.firstName,
        'Last Name': user.lastName,
        Email: user.email,
        'Mobile Number': user.mobileNo,
      };
      return userDetails;
    });

    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';
    const ws = XLSX.utils.json_to_sheet(excelDate);
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, 'User Details' + fileExtension);
  };

  return (
    <div className={styles.root_div}>
      {loading ? (
        <Loading text='Loading' />
      ) : (
        <div>
          <PageHeader text='User Details' />
          {userLoadingError ? (
            <p>{userLoadingError}</p>
          ) : (
            <div>
              <div className={styles.filter_btn_group}>
                <div className={styles.filter_btn}>
                  <Filter
                    title='User'
                    userDetailsFilter
                    filterData={userData}
                    filterLoading={filterLoading}
                    submitFilterDate={handleUserDetailsFilter}
                    handleExportDate={handleExportDate}
                    onReset={handleResetFilter}
                  />
                </div>
                <Button onClick={handleDownload}>Download</Button>
              </div>

              <DataTable
                tableData={paginatedUserData}
                tableheading={tableHeading}
                handleOnClick={() => {}}
                tag='userDetails'
                fetchError=''
                search_filter_loading={filterLoading}
              />
              {userData.length > PAGE_SIZE && (
                <Pagination
                  pageCount={pageCount}
                  currentPage={currentPage}
                  onChange={handlePageChange}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};
export default connect(mapStateToProps)(UserDetails);
